import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
  getMeeting,
  createMeeting,
  updateMeeting,
  overrideMeeting,
  startMeeting,
  completeMeeting,
  reopenMeeting,
} from "../api/meeting-api";
import type {
  Meeting,
  CreateMeetingRequest,
  MeetingOverrideRequest,
  MeetingReopenRequest,
} from "../types/meeting.type";

const STALE_TIME = 30_000;

const meetingKeys = {
  meeting: (rfxId: number, evalId: number) =>
    ["meeting", rfxId, evalId] as const,
};

interface UseMeetingResult {
  completeMeeting: UseMutationResult<Meeting, Error, void>;
  createMeeting: UseMutationResult<Meeting, Error, CreateMeetingRequest>;
  isLoading: boolean;
  meeting: Meeting | undefined;
  overrideMeeting: UseMutationResult<Meeting, Error, MeetingOverrideRequest>;
  reopenMeeting: UseMutationResult<Meeting, Error, MeetingReopenRequest>;
  startMeeting: UseMutationResult<Meeting, Error, void>;
  updateMeeting: UseMutationResult<Meeting, Error, CreateMeetingRequest>;
}

export const useMeeting = (
  rfxId: number | undefined,
  evalId: number | undefined
): UseMeetingResult => {
  const queryClient = useQueryClient();

  const query: UseQueryResult<Meeting> = useQuery({
    queryKey: meetingKeys.meeting(rfxId!, evalId!),
    queryFn: () => getMeeting(rfxId!, evalId!),
    enabled: !!rfxId && !!evalId,
    staleTime: STALE_TIME,
  });

  const invalidateMeeting = () => {
    queryClient.invalidateQueries({
      queryKey: meetingKeys.meeting(rfxId!, evalId!),
    });
  };

  const createMeetingMutation = useMutation({
    mutationFn: (payload: CreateMeetingRequest) =>
      createMeeting(rfxId!, evalId!, payload),
    onSuccess: invalidateMeeting,
  });

  const updateMeetingMutation = useMutation({
    mutationFn: (payload: CreateMeetingRequest) =>
      updateMeeting(rfxId!, evalId!, payload),
    onSuccess: invalidateMeeting,
  });

  const overrideMeetingMutation = useMutation({
    mutationFn: (payload: MeetingOverrideRequest) =>
      overrideMeeting(rfxId!, evalId!, payload),
    onSuccess: invalidateMeeting,
  });

  const startMeetingMutation = useMutation({
    mutationFn: () => startMeeting(rfxId!, evalId!),
    onSuccess: invalidateMeeting,
  });

  const completeMeetingMutation = useMutation({
    mutationFn: () => completeMeeting(rfxId!, evalId!),
    onSuccess: invalidateMeeting,
  });

  const reopenMeetingMutation = useMutation({
    mutationFn: (payload: MeetingReopenRequest) =>
      reopenMeeting(rfxId!, evalId!, payload),
    onSuccess: invalidateMeeting,
  });

  return {
    meeting: query.data,
    isLoading: query.isLoading,
    createMeeting: createMeetingMutation,
    updateMeeting: updateMeetingMutation,
    overrideMeeting: overrideMeetingMutation,
    startMeeting: startMeetingMutation,
    completeMeeting: completeMeetingMutation,
    reopenMeeting: reopenMeetingMutation,
  };
};
